import { GanttTask, Partner } from '../../types/gantt.types'
import { SiteInfo } from '../../views/CalendarMultiView'
import { SigongAssignUpdate, SigongPopup, SigongTask } from '../../views/SigongPopup'
import { NavFn } from '../../pages/SchedulerPage'
import { MaterialOrderChange, MaterialOrderPopup } from '../../views/MaterialOrderPopup'
import { ShipStatusPopup } from '../ShipStatusPopup'
import { FieldReportStatusPopup } from '../FieldReportStatusPopup'
import { AddTaskPopup } from '../../views/AddTaskPopup'
import { DateChange, ScheduleChangeNoticeItemResult } from '../../api/foremanApi'
import { ScheduleChangeNoticeModal } from '../ScheduleChangeNoticeModal'
// 시공·자재파트너·배송정보현황·공사현황 팝업 호스트

export type GanttPopupHostProps = {
  showSigongPanel: boolean
  tasks: GanttTask[]
  partners: Partner[]
  selectedSite: SiteInfo
  setShowSigongPanel: React.Dispatch<React.SetStateAction<boolean>>
  fetchPartners: () => void
  handleForemanAssigned: (u: SigongAssignUpdate) => void
  embedded: boolean | undefined
  navigate: NavFn
  showMaterialPanel: boolean
  materialFocusTaskId: number | null
  setShowMaterialPanel: React.Dispatch<React.SetStateAction<boolean>>
  handleMaterialChanged: (c: MaterialOrderChange) => void
  showShipPanel: boolean
  setShowShipPanel: React.Dispatch<React.SetStateAction<boolean>>
  setDeepLinkTaskId: React.Dispatch<React.SetStateAction<number | null>>
  deepLinkTaskId: number | null
  showStatusPanel: boolean
  setShowStatusPanel: React.Dispatch<React.SetStateAction<boolean>>
}

export function GanttPopupHost({
  showSigongPanel, tasks, partners, selectedSite, setShowSigongPanel, fetchPartners, handleForemanAssigned,
  embedded, navigate, showMaterialPanel, materialFocusTaskId, setShowMaterialPanel, handleMaterialChanged,
  showShipPanel, setShowShipPanel, setDeepLinkTaskId, deepLinkTaskId, showStatusPanel, setShowStatusPanel,
}: GanttPopupHostProps) {
  return (
    <>
      {showSigongPanel && (
        <SigongPopup
          tasks={tasks as unknown as SigongTask[]}
          partners={partners}
          siteName={selectedSite?.siteName}
          scheduleId={selectedSite?.scheduleId}
          onClose={() => setShowSigongPanel(false)}
          onPartnerCreated={fetchPartners}
          onAssigned={handleForemanAssigned}
          onOpenSettings={embedded ? undefined : () => navigate('settings-foreman-confirm')}
        />
      )}

      {/* ── 자재파트너배정·발주정보발송 팝업 (C-3) ──────────────────── */}
      {showMaterialPanel && selectedSite?.scheduleId != null && (
        <MaterialOrderPopup
          siteId={selectedSite.siteId}
          scheduleId={selectedSite.scheduleId}
          siteName={selectedSite.siteName}
          focusTaskId={materialFocusTaskId}
          onClose={() => setShowMaterialPanel(false)}
          onChanged={handleMaterialChanged}
          onOpenOrderSettings={embedded ? undefined : () => navigate('settings-order')}
        />
      )}

      {/* ── 배송정보현황·요청 팝업 (C-4) ─────────────────── */}
      {showShipPanel && selectedSite?.scheduleId != null && (
        <ShipStatusPopup
          onClose={() => { setShowShipPanel(false); setDeepLinkTaskId(null) }}
          scheduleId={selectedSite.scheduleId}
          siteName={selectedSite.siteName}
          focusTaskId={deepLinkTaskId}
          taskNameById={new Map(tasks.map(t => [t.taskId, t.workTypeName] as const))}
          vendorConstructTaskIds={tasks.filter(t => t.constructByVendor).map(t => t.taskId)}
          onOpenForemanAssign={() => setShowSigongPanel(true)}
        />
      )}

      {/* ── 공사현황보기 팝업 (C-1-11) ──────────────────────────────── */}
      {showStatusPanel && (
        <FieldReportStatusPopup
          onClose={() => setShowStatusPanel(false)}
          siteId={selectedSite?.siteId}
          siteName={selectedSite?.siteName}
        />
      )}
    </>
  )
}

// 공종 추가 팝업

export type GanttAddTaskHostProps = {
  showAddPop: boolean
  selectedSite: SiteInfo
  setShowAddPop: React.Dispatch<React.SetStateAction<boolean>>
  setAddPopDefaults: React.Dispatch<React.SetStateAction<{ workTypeCode?: string; predecessorId: number; defaultStartDate?: string; } | null>>
  partners: Partner[]
  addPopDefaults: { workTypeCode?: string; predecessorId: number; defaultStartDate?: string; } | null
  insertAfterIdRef: React.MutableRefObject<number | null>
  prevTaskIdsRef: React.MutableRefObject<Set<number>>
  tasksRef: React.MutableRefObject<GanttTask[]>
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>
}

export function GanttAddTaskHost({
  showAddPop, selectedSite, setShowAddPop, setAddPopDefaults, partners, addPopDefaults, insertAfterIdRef,
  prevTaskIdsRef, tasksRef, setRefreshKey,
}: GanttAddTaskHostProps) {
  return (
    <>
      {showAddPop && selectedSite && (
        <AddTaskPopup
          onClose={() => { setShowAddPop(false); setAddPopDefaults(null) }}
          selectedSite={selectedSite}
          partners={partners}
          onSaved={() => {
            if (addPopDefaults?.predecessorId) {
              insertAfterIdRef.current = addPopDefaults.predecessorId
              prevTaskIdsRef.current = new Set(tasksRef.current.map(t => t.taskId))
            }
            setRefreshKey(k => k + 1)
            setAddPopDefaults(null)
          }}
          defaultWorkTypeCode={addPopDefaults?.workTypeCode}
          existingWorkTypeCodes={tasksRef.current.map(t => t.workTypeCode)}
          defaultStartDate={addPopDefaults?.defaultStartDate}
        />
      )}
    </>
  )
}

// 일정 변경 카톡 확인 창(날짜 저장 직후, 확정·회신 대기 반장 대상)

export type GanttChangeNoticeHostProps = {
  changeNotice: { scheduleId: number; changes: DateChange[]; } | null
  tasksRef: React.MutableRefObject<GanttTask[]>
  handleChangeNoticeSent: (sentItems: ScheduleChangeNoticeItemResult[]) => void
  setChangeNotice: React.Dispatch<React.SetStateAction<{ scheduleId: number; changes: DateChange[]; } | null>>
}

export function GanttChangeNoticeHost({
  changeNotice, tasksRef, handleChangeNoticeSent, setChangeNotice,
}: GanttChangeNoticeHostProps) {
  return (
    <>
      {changeNotice && (
        <ScheduleChangeNoticeModal
          scheduleId={changeNotice.scheduleId}
          changes={changeNotice.changes}
          colorOf={(taskId) => tasksRef.current.find(t => t.taskId === taskId)?.color}
          onSent={handleChangeNoticeSent}
          onClose={() => setChangeNotice(null)}
        />
      )}
    </>
  )
}
